import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Badge, Button, StatusDot } from '@/components/atoms';
import {
  useAddManifestation,
  useDeleteStory,
  useRemoveManifestation,
  useStory,
  useUpdateStory,
} from '@/hooks/useStories';
import type { ManifestationKind } from '@/types/story';

import styles from './StoryDetail.module.css';

const KIND_OPTIONS: { value: ManifestationKind; label: string }[] = [
  { value: 'wp_post', label: 'WP Post (artigo)' },
  { value: 'tweet', label: 'Tweet' },
  { value: 'dalle_image', label: 'Imagem DALL-E' },
  { value: 'horoscope_sign', label: 'Signo (horóscopo)' },
  { value: 'other', label: 'Outro' },
];

const KIND_LABEL: Record<ManifestationKind, string> = {
  wp_post: 'artigo',
  tweet: 'tweet',
  dalle_image: 'imagem',
  horoscope_sign: 'signo',
  other: 'outro',
};

export function StoryDetail() {
  const params = useParams<{ id: string }>();
  const id = params.id!;
  const navigate = useNavigate();
  const { data: story, isLoading } = useStory(id);
  const updateMutation = useUpdateStory();
  const deleteMutation = useDeleteStory();
  const addManif = useAddManifestation();
  const removeManif = useRemoveManifestation();

  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const [newKind, setNewKind] = useState<ManifestationKind>('wp_post');
  const [newExternalId, setNewExternalId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');

  useEffect(() => {
    if (story) {
      setEditTitle(story.title);
      setEditDesc(story.description ?? '');
    }
  }, [story]);

  if (isLoading) return <p className={styles.muted}>A carregar…</p>;
  if (!story) return <p>Story não encontrada.</p>;

  async function handleSave() {
    try {
      await updateMutation.mutateAsync({
        id,
        payload: { title: editTitle, description: editDesc || undefined },
      });
      setEditing(false);
    } catch {
      // hook handles error state
    }
  }

  async function handleArchive() {
    await updateMutation.mutateAsync({
      id,
      payload: { status: story!.status === 'active' ? 'archived' : 'active' },
    });
  }

  async function handleDelete() {
    if (!confirm('Eliminar story permanentemente?')) return;
    try {
      await deleteMutation.mutateAsync(id);
      navigate('/stories');
    } catch {
      // hook handles error state
    }
  }

  async function handleAddManif() {
    if (!newExternalId.trim()) return;
    try {
      await addManif.mutateAsync({
        storyId: id,
        payload: {
          kind: newKind,
          external_id: newExternalId.trim(),
          external_url: newUrl.trim() || undefined,
          label: newLabel.trim() || undefined,
        },
      });
      setNewExternalId('');
      setNewLabel('');
      setNewUrl('');
    } catch {
      // hook handles error
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <Link to="/stories" className={styles.back}>
          ← Stories
        </Link>
        {editing ? (
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className={styles.titleInput}
          />
        ) : (
          <h1 className={styles.title}>{story.title}</h1>
        )}
        <Badge tone={story.status === 'active' ? 'ok' : 'neutral'} size="sm">
          {story.status}
        </Badge>
      </header>

      {editing ? (
        <textarea
          value={editDesc}
          onChange={(e) => setEditDesc(e.target.value)}
          rows={3}
          placeholder="Descrição"
          className={styles.descInput}
        />
      ) : (
        story.description && <p className={styles.desc}>{story.description}</p>
      )}

      <div className={styles.actions}>
        {editing ? (
          <>
            <Button onClick={handleSave} loading={updateMutation.isPending}>
              Guardar
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancelar
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Editar
            </Button>
            <Button
              variant="ghost"
              onClick={handleArchive}
              loading={updateMutation.isPending}
            >
              {story.status === 'active' ? 'Arquivar' : 'Reactivar'}
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              Eliminar
            </Button>
          </>
        )}
      </div>

      <h2 className={styles.sectionTitle}>
        Manifestações ({story.manifestations.length})
      </h2>

      <div className={styles.addCard}>
        <p className={styles.addCardTitle}>Ligar nova manifestação</p>
        <div className={styles.addCardGrid}>
          <select
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as ManifestationKind)}
            className={styles.select}
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="ID externo (ex: post WP id, tweet id)"
            value={newExternalId}
            onChange={(e) => setNewExternalId(e.target.value)}
            className={styles.input}
          />
          <input
            type="text"
            placeholder="Label (opcional)"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            className={styles.input}
          />
          <input
            type="url"
            placeholder="URL (opcional)"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            className={styles.input}
          />
          <Button
            onClick={handleAddManif}
            disabled={!newExternalId.trim()}
            loading={addManif.isPending}
          >
            Ligar
          </Button>
        </div>
      </div>

      <ul className={styles.manifList}>
        {story.manifestations.length === 0 && (
          <li className={styles.empty}>
            Nenhuma manifestação ainda. Liga uma acima.
          </li>
        )}
        {story.manifestations.map((m) => (
          <li key={m.id} className={styles.manifRow}>
            <span className={styles.kindLabel}>
              {KIND_LABEL[m.kind] ?? 'outro'}
            </span>
            <div className={styles.manifBody}>
              <div className={styles.manifTitle}>
                <Badge tone="info" size="xs">
                  {m.kind}
                </Badge>
                <strong>{m.label ?? `#${m.external_id}`}</strong>
              </div>
              <div className={styles.manifMeta}>
                <span>id: {m.external_id}</span>
                {m.site && <span>site: {m.site}</span>}
                <span>
                  ligada{' '}
                  {new Date(m.linked_at).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>
            {m.kind === 'wp_post' && (
              <Link to={`/artigos/${m.external_id}`} className={styles.smallLink}>
                Abrir editor →
              </Link>
            )}
            {m.external_url && (
              <a
                href={m.external_url}
                target="_blank"
                rel="noreferrer"
                className={styles.smallLink}
              >
                Externo ↗
              </a>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                removeManif.mutate({ storyId: id, manifestationId: m.id })
              }
            >
              <StatusDot state="crit" /> Remover
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
